import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Trash2, Send } from "lucide-react";
import { useContent } from "@/hooks/useContent";
import { PillarIcon } from "@/components/PillarIcon";
import { MoodChart } from "@/components/MoodChart";
import { WeeklyEncouragement } from "@/components/WeeklyEncouragement";

export function Diario() {
  const { data: content } = useContent();
  const [text, setText] = useState("");
  const [mood, setMood] = useState(null);
  const [pillar, setPillar] = useState(null);
  const [entries, setEntries] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { setEntries((await api.get("/journal")).data); } catch {}
  };
  useEffect(() => { load(); }, []);

  const moods = content?.moods || [];
  const pillars = content?.pillars || {};
  const pillarOrder = content?.pillar_order || [];
  const moodById = Object.fromEntries(moods.map((m) => [m.id, m]));

  const save = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/journal", { text, mood, pillar });
      setEntries((e) => [data, ...e]);
      setText(""); setMood(null); setPillar(null);
      toast.success("Pensiero salvato");
    } catch {
      toast.error("Errore nel salvataggio");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    setEntries((e) => e.filter((x) => x.id !== id));
    try { await api.delete(`/journal/${id}`); } catch {}
  };

  const fmt = (iso) => {
    try {
      return new Date(iso).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch { return iso; }
  };

  // mood summary for "progressi nel tempo"
  const moodCounts = entries.reduce((acc, e) => {
    if (e.mood) acc[e.mood] = (acc[e.mood] || 0) + 1;
    return acc;
  }, {});
  const topMoods = Object.entries(moodCounts).sort((a, b) => b[1] - a[1]);

  return (
    <div className="bw-fade">
      <div className="mb-5">
        <p className="text-xs uppercase tracking-widest text-[#EAC6D9] font-semibold">Il tuo diario</p>
        <h1 className="font-serif text-4xl font-bold text-[#F6DCEA]">Scrivi per te</h1>
      </div>

      {/* Frase della settimana personalizzata */}
      <WeeklyEncouragement entries={entries} moods={moods} />

      <div className="rounded-2xl bg-[#2A0A1B] border border-[#E32C8C]/35 bw-card p-4 mb-6">
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Cosa senti oggi? Un pensiero, una gratitudine, un piccolo passo…"
          data-testid="diario-input"
          rows={4}
          className="w-full bg-transparent resize-none text-base text-[#F6DCEA] placeholder:text-[#EAC6D9]/60 focus:outline-none"
        />

        {/* Mood picker */}
        <p className="text-xs font-semibold text-[#EAC6D9] uppercase tracking-wider mt-2 mb-2">Come ti senti?</p>
        <div className="flex flex-wrap gap-2 mb-3">
          {moods.map((m) => {
            const active = mood === m.id;
            return (
              <button
                key={m.id}
                type="button"
                onClick={() => setMood(active ? null : m.id)}
                data-testid={`diario-mood-${m.id}`}
                className="text-sm font-medium rounded-full px-3.5 py-2 border transition-all active:scale-95"
                style={{
                  borderColor: m.color,
                  backgroundColor: active ? m.color : "transparent",
                  color: active ? "#fff" : m.color,
                }}
              >
                {m.label}
              </button>
            );
          })}
        </div>

        {/* Pillar picker */}
        <p className="text-xs font-semibold text-[#EAC6D9] uppercase tracking-wider mb-2">A quale area?</p>
        <div className="flex flex-wrap gap-2 mb-4">
          {pillarOrder.map((p) => {
            const meta = pillars[p];
            const active = pillar === p;
            return (
              <button
                key={p}
                type="button"
                onClick={() => setPillar(active ? null : p)}
                data-testid={`diario-pillar-${p}`}
                className="text-sm font-medium rounded-full px-3.5 py-2 border transition-all active:scale-95 flex items-center gap-1.5"
                style={{
                  borderColor: meta.color,
                  backgroundColor: active ? meta.color : "transparent",
                  color: active ? "#fff" : meta.color,
                }}
              >
                <PillarIcon pillar={p} className="w-3.5 h-3.5" />
                {meta.name}
              </button>
            );
          })}
        </div>

        <button
          onClick={save}
          disabled={busy || !text.trim()}
          data-testid="diario-save-btn"
          className="w-full bg-gradient-to-r from-[#E32C8C] to-[#8E1A52] text-white font-semibold py-3 px-6 rounded-full shadow-lg shadow-[#E32C8C]/25 bw-glow hover:opacity-95 active:scale-[0.98] transition-all flex items-center justify-center gap-2 disabled:opacity-50"
        >
          <Send className="w-4 h-4" /> Salva pensiero
        </button>
      </div>

      {/* Andamento umore settimanale */}
      <MoodChart entries={entries} moods={moods} />

      {/* Progressi nel tempo */}
      {topMoods.length > 0 && (
        <div className="rounded-2xl bg-[#2A0A1B] border border-[#E32C8C]/35 bw-card p-4 mb-6" data-testid="diario-progress">
          <p className="text-xs font-semibold text-[#EAC6D9] uppercase tracking-wider mb-3">I tuoi stati d'animo</p>
          <div className="space-y-2">
            {topMoods.map(([id, count]) => {
              const m = moodById[id];
              if (!m) return null;
              const pct = (count / entries.length) * 100;
              return (
                <div key={id} className="flex items-center gap-3">
                  <span className="text-sm font-medium w-24 shrink-0" style={{ color: m.color }}>{m.label}</span>
                  <div className="flex-1 h-2 rounded-full bg-[#6B2247] overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: m.color }} />
                  </div>
                  <span className="text-xs font-bold text-[#EAC6D9] w-5 text-right">{count}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {entries.length === 0 ? (
        <p className="text-center text-[#EAC6D9] text-sm mt-10" data-testid="diario-empty">
          Il tuo diario è ancora vuoto. Scrivi il tuo primo pensiero. 💗
        </p>
      ) : (
        <div className="space-y-3">
          {entries.map((e) => {
            const m = e.mood ? moodById[e.mood] : null;
            const pm = e.pillar ? pillars[e.pillar] : null;
            return (
              <div key={e.id} data-testid={`diario-entry-${e.id}`} className="rounded-2xl bg-[#2A0A1B] border border-[#E32C8C]/35 bw-card p-4">
                <div className="flex items-start justify-between gap-3">
                  <p className="text-[15px] text-[#F6DCEA] leading-relaxed whitespace-pre-line flex-1">{e.text}</p>
                  <button onClick={() => remove(e.id)} data-testid={`diario-delete-${e.id}`} className="text-[#EAC6D9] hover:text-[#8E1A52] shrink-0">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                {(m || pm) && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {m && (
                      <span className="text-xs font-semibold rounded-full px-2.5 py-1 text-white" style={{ backgroundColor: m.color }}>
                        {m.label}
                      </span>
                    )}
                    {pm && (
                      <span className="text-xs font-semibold rounded-full px-2.5 py-1 flex items-center gap-1" style={{ color: pm.color, backgroundColor: `${pm.color}1A` }}>
                        <PillarIcon pillar={e.pillar} className="w-3 h-3" /> {pm.name}
                      </span>
                    )}
                  </div>
                )}
                <p className="text-xs text-[#EAC6D9] mt-2">{fmt(e.created_at)}</p>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
